import { initializeApp } from 'firebase/app';
import { doc, getDoc, initializeFirestore } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import type { FaqLink, Tenant, TicketType } from '@dycs/shared';

// 위젯은 공개(인증 없음). tenants 공개 read + Function(askCsBot/escalate) 경유 write.
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

export const app = initializeApp(firebaseConfig);
export const db = initializeFirestore(app, { ignoreUndefinedProperties: true });
export const functions = getFunctions(app, 'asia-northeast3');

/** URL 쿼리에서 tenantId·embedToken·진입 모드 추출(임베드 식별). */
export function readEmbedParams(): {
  tenantId: string | null;
  embedToken: string | null;
  directIntake: boolean;
  entryType: TicketType | null;
  /** `?type=praise` — 칭찬 릴레이 창구 고정 링크(tenants.praiseRelay 거래처만 열린다). */
  entryPraise: boolean;
  /** `?type=quote` — 견적 접수 고정 링크(QUOTE_TENANTS 창구만 열린다). `&kind=commute|tour` 로 구분 지정. */
  entryQuote: boolean;
  quoteKind: 'commute' | 'tour' | null;
} {
  const p = new URLSearchParams(window.location.search);
  const type = p.get('type');
  return {
    tenantId: p.get('tenant') ?? p.get('tenantId'),
    embedToken: p.get('token') ?? p.get('embedToken'),
    // intake=1 — 챗봇을 건너뛰고 바로 접수 폼. 카카오 채널의 '접수하기' 버튼처럼
    //   **이미 챗봇을 거친 뒤** 넘어오는 진입점이 쓴다(챗봇 재노출 = 제자리걸음).
    directIntake: p.get('intake') === '1',
    // type=inquiry|lost_item — 유형 선택 화면을 건너뛰고 그 유형으로 고정 진입.
    //   거래처에 '고객문의 창구'·'분실물 창구' 링크를 따로 배포할 때 쓴다(고정 링크엔 유형 변경 없음).
    //   값 없음/모르는 값 = null → 기존 선택 화면 그대로(하위 호환, 기존 ?tenant= 링크 무영향).
    entryType: type === 'inquiry' || type === 'lost_item' ? type : null,
    // 🔴 entryType 과 따로 둔다 — entryType 의 값 범위(TicketType)를 넓히면 기존 분기 전부가
    //    'praise' 를 문의/분실물 중 하나로 오인할 수 있다. 예전 번들엔 'praise' 가 null(=선택 화면).
    entryPraise: type === 'praise',
    // 🔴 praise 와 같은 이유로 entryType 밖에 둔다 — 옛 번들엔 'quote' 가 null(=선택 화면)이다.
    entryQuote: type === 'quote',
    quoteKind: p.get('kind') === 'tour' ? 'tour' : p.get('kind') === 'commute' ? 'commute' : null,
  };
}

/**
 * tenants/{tenantId} 공개 read(active·displayName·brandColor·routes·routeSchedule).
 *   routeSchedule(노선·출발시간표)은 문서를 통째로 읽으므로 따로 고를 필드가 없다 — 노선·시각은 공개 정보.
 *   비활성/없음/임베드토큰 불일치 → null(위젯 graceful 안내).
 */
export async function loadTenant(
  tenantId: string,
  embedToken: string | null,
): Promise<Tenant | null> {
  try {
    const snap = await getDoc(doc(db, 'tenants', tenantId));
    if (!snap.exists()) return null;
    const t = { id: snap.id, ...(snap.data() as Omit<Tenant, 'id'>) };
    if (t.active === false) return null;
    // embedToken 검증 — 거래처가 토큰을 설정했고 위젯에 전달됐다면 일치해야 함.
    //   (토큰 미설정 거래처는 tenantId 만으로 접근 허용 — 점진 운영.)
    if (t.embedToken && embedToken && t.embedToken !== embedToken) return null;
    return t;
  } catch (err) {
    console.error('[widget] tenant 로드 실패', err);
    return null;
  }
}

// ────────────────────────────────────────────────────────────
// Functions 콜러블 (위젯 공개 호출 — invoker:'public')
// ────────────────────────────────────────────────────────────

/** 챗봇 대화 한 턴 — 위젯이 보관하고 매 호출·접수에 동봉한다. */
export interface ChatTurn {
  role: 'customer' | 'bot';
  text: string;
}

export interface AskCsBotRequest {
  tenantId: string;
  text: string;
  threadId?: string;
  /** 직전 대화 — 없으면 서버가 단일 턴으로 답해 대화가 끊긴다. */
  history?: ChatTurn[];
}
export interface AskCsBotResponse {
  threadId: string;
  answer: string;
  tier: 'gemini-strong' | 'gemini-weak' | 'suggestions' | 'fallback';
  faqRefs?: string[];
  suggestions?: string[];
  category?: string;
  /** 참조 FAQ 의 정류장 지도 링크(강매치 시). 본문과 분리돼 URL 이 변형되지 않는다. */
  links?: FaqLink[];
}

const _askCsBot = httpsCallable<AskCsBotRequest, AskCsBotResponse>(functions, 'askCsBot', {
  timeout: 120_000, // Gemini backoff 누적(SPEC §4) — 클라 타임아웃 120s 명시.
});
export function askCsBot(req: AskCsBotRequest): Promise<AskCsBotResponse> {
  return _askCsBot(req).then((r) => r.data);
}

export interface EscalateRequest {
  tenantId: string;
  type: 'inquiry' | 'lost_item';
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  content: string;
  route?: string;
  privacyConsent: boolean;
  fromChatbot?: boolean;
  /** 접수 전 챗봇 대화 — 상담원 스레드 맨 앞에 그대로 남는다. */
  chatHistory?: ChatTurn[];
  category?: string;
  itemName?: string;
  lostDate?: string;
  /** 분실 시간대(`LOST_TIME_SLOTS` 값). 어느 회차 차량인지는 날짜만으론 안 나온다. */
  lostTime?: string;
  /** 출발 시간 — 거래처 시간표(routeSchedule)에서 고른 값 그대로(2026-10-13). */
  departureTime?: string;
  lostLocation?: string;
  /** 고객사(홈페이지 창구 — 어느 거래처 셔틀인지 티켓만으론 모를 때). */
  companyName?: string;
  /** 거래처 전용 식별번호(사번·학번 — tenants.employeeNoLabel 이 켜진 창구만). */
  employeeNo?: string;
  /** 사업장(tenants.sites 가 있는 거래처 — 폼 필수). */
  site?: string;
  /** 칭찬 릴레이 창구 접수 — 이름·연락처 대신 사번·노선 필수(서버가 거래처 설정을 다시 본다). */
  praiseRelay?: boolean;
  /** 칭찬 대상 기사명(선택). */
  praiseDriverName?: string;
  /** 휴대폰 인증 결과(requirePhoneVerify 거래처에서 필수). */
  verifyId?: string;
  verifyToken?: string;
}
export interface EscalateResponse {
  ticketId: string;
}

const _escalate = httpsCallable<EscalateRequest, EscalateResponse>(functions, 'escalate', {
  timeout: 60_000,
});
export function escalate(req: EscalateRequest): Promise<EscalateResponse> {
  return _escalate(req).then((r) => r.data);
}

// ────────────────────────────────────────────────────────────
// 전세버스 견적 접수 (2026-10-18) — tickets 가 아니라 `quotes` 로 간다(서버 `quote.ts`).
// ────────────────────────────────────────────────────────────

export interface SubmitQuoteRequest {
  tenantId: string;
  kind: 'commute' | 'tour';
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  companyName?: string;
  vehicleType?: string;
  vehicleCount?: number;
  passengers?: number;
  routeRegion?: string;
  routeCount?: string;
  startDate?: string;
  departRegion?: string;
  departDate?: string;
  arriveRegion?: string;
  arriveDate?: string;
  departTime?: string;
  arriveTime?: string;
  referral?: string;
  note?: string;
  privacyConsent: boolean;
  /** 봇 덫 칸 값(사람이면 비어 있다). */
  website?: string;
}

const _submitQuote = httpsCallable<SubmitQuoteRequest, { quoteId: string }>(functions, 'submitQuote', {
  timeout: 30_000,
});
export function submitQuote(req: SubmitQuoteRequest): Promise<{ quoteId: string }> {
  return _submitQuote(req).then((r) => r.data);
}

// ────────────────────────────────────────────────────────────
// 휴대폰 인증 (tenants.requirePhoneVerify 창구 — 홈페이지 고객민원)
//   🔴 인증번호 원문은 서버만 안다(해시 저장). 위젯은 발송을 요청하고 입력값을 되돌려줄 뿐이다.
// ────────────────────────────────────────────────────────────

export interface RequestPhoneCodeRequest {
  tenantId: string;
  phone: string;
}
export interface RequestPhoneCodeResponse {
  verifyId: string;
  channel: 'alimtalk' | 'sms';
  expiresInSec: number;
}

const _requestPhoneCode = httpsCallable<RequestPhoneCodeRequest, RequestPhoneCodeResponse>(
  functions,
  'requestPhoneCode',
  { timeout: 60_000 },
);
export function requestPhoneCode(req: RequestPhoneCodeRequest): Promise<RequestPhoneCodeResponse> {
  return _requestPhoneCode(req).then((r) => r.data);
}

export interface VerifyPhoneCodeRequest {
  verifyId: string;
  code: string;
}
export interface VerifyPhoneCodeResponse {
  verifyToken: string;
  phone: string;
}

const _verifyPhoneCode = httpsCallable<VerifyPhoneCodeRequest, VerifyPhoneCodeResponse>(
  functions,
  'verifyPhoneCode',
  { timeout: 30_000 },
);
export function verifyPhoneCode(req: VerifyPhoneCodeRequest): Promise<VerifyPhoneCodeResponse> {
  return _verifyPhoneCode(req).then((r) => r.data);
}

/**
 * Functions 오류에서 **서버가 고객에게 보내려던 문구**를 꺼낸다.
 *   🔴 httpsCallable 오류를 통째로 "실패했습니다"로 뭉개면, 남은 시도 횟수·재발송 대기 시간
 *      같은 정작 필요한 안내가 전부 사라진다(카카오 스킬에서 이미 겪은 실수).
 */
export function callableMessage(err: unknown, fallback: string): string {
  const m = (err as { message?: string } | null)?.message;
  if (typeof m === 'string' && m.trim() && !/^internal$/i.test(m.trim())) return m.trim();
  return fallback;
}

// ────────────────────────────────────────────────────────────
// 「이어서 문의하기」(②번 · 2026-09-09)
//   회신 알림/메일의 링크 `?f=<키>` 로 들어오면 **같은 티켓을 열어** 이어 쓴다.
//   🔴 Firestore 를 직접 읽지 않는다 — 티켓을 공개로 열면 «가린다»는 약속이 화면 장식이 된다
//      (필드 단위 read 제어가 없다). 아래 두 onCall 이 화이트리스트로 재조립해 내려 준다.
// ────────────────────────────────────────────────────────────

/** URL 에서 이어쓰기 키(`?f=`) 추출. 없으면 null(기존 진입과 완전히 무관). */
export function readFollowUpKey(): string | null {
  const v = new URLSearchParams(window.location.search).get('f');
  return v && v.trim() ? v.trim() : null;
}

export interface FollowUpMessage {
  role: 'customer' | 'agent' | 'bot';
  text: string;
  atMs: number;
}

export type OpenFollowUpResult =
  | { ok: false; reason: 'bad_key' | 'not_found' | 'expired' }
  | {
      ok: true;
      tenantId: string;
      tenantName: string;
      brandColor: string;
      receiptNo: string;
      type: TicketType;
      status: string;
      customerName: string;
      itemName: string;
      createdAtMs: number;
      appendCount: number;
      maxAppends: number;
      messages: FollowUpMessage[];
    };

/** 링크 키로 문의 1건 열기(공개 onCall). 실패는 **사유를 구분해** 돌려준다. */
export async function openFollowUp(f: string): Promise<OpenFollowUpResult> {
  const call = httpsCallable<{ f: string }, OpenFollowUpResult>(functions, 'openFollowUp');
  const res = await call({ f });
  return res.data;
}

/** 같은 티켓에 고객 메시지 이어 붙이기(공개 onCall). */
export async function appendFollowUp(f: string, text: string): Promise<{ ok: true; receiptNo: string }> {
  const call = httpsCallable<{ f: string; text: string }, { ok: true; receiptNo: string }>(
    functions,
    'appendFollowUp',
  );
  const res = await call({ f, text });
  return res.data;
}
